import { useState, useEffect, useMemo } from 'react'
import {
  Mail,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Download,
  Eye,
  X,
  FileText,
  CheckCircle,
  Printer,
  Send,
  Globe,
  ExternalLink,
  CreditCard,
  Gift,
  Clock,
  Loader2,
} from 'lucide-react'
import api from '@/services/api'
import toast from 'react-hot-toast'
import type {
  ClientProfile,
  ClientPortalLetter,
  ClientPortalLetterDetail,
  ClientAdditionalLetter,
} from '@/types'

const STATUS_CONFIG: Record<string, { bg: string; text: string; label: string; icon: typeof CheckCircle }> = {
  approved: {
    bg: 'bg-blue-500/10',
    text: 'text-blue-400',
    label: 'Approved',
    icon: CheckCircle,
  },
  printed: {
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-400',
    label: 'Printed',
    icon: Printer,
  },
  mailed: {
    bg: 'bg-purple-500/10',
    text: 'text-purple-400',
    label: 'Mailed',
    icon: Send,
  },
  additional: {
    bg: 'bg-orange-500/10',
    text: 'text-orange-400',
    label: 'Available',
    icon: Gift,
  },
  additional_requested: {
    bg: 'bg-yellow-500/10',
    text: 'text-yellow-400',
    label: 'Requested',
    icon: Clock,
  },
}

function formatDate(dateStr?: string) {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatWeekRange(start: string, end: string) {
  const s = new Date(start)
  const e = new Date(end)
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
  const yearOpts: Intl.DateTimeFormatOptions = { ...opts, year: 'numeric' }
  return `${s.toLocaleDateString('en-GB', opts)} - ${e.toLocaleDateString('en-GB', yearOpts)}`
}

export default function ClientDashboard() {
  const [profile, setProfile] = useState<ClientProfile | null>(null)
  const [letters, setLetters] = useState<ClientPortalLetter[]>([])
  const [weekStart, setWeekStart] = useState('')
  const [weekEnd, setWeekEnd] = useState('')
  const [weekOffset, setWeekOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [selectedLetter, setSelectedLetter] = useState<ClientPortalLetterDetail | null>(null)
  const [letterLoading, setLetterLoading] = useState(false)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const [additionalLetters, setAdditionalLetters] = useState<ClientAdditionalLetter[]>([])
  const [additionalLoading, setAdditionalLoading] = useState(false)
  const [requestingId, setRequestingId] = useState<number | null>(null)

  // Derive portfolio images array from profile
  const portfolioImages = useMemo(() => [
    profile?.portfolioImage1 || null,
    profile?.portfolioImage2 || null,
    profile?.portfolioImage3 || null,
  ], [profile])

  useEffect(() => {
    fetchProfile()
    fetchAdditionalLetters()
  }, [])

  useEffect(() => {
    fetchLetters()
  }, [weekOffset])

  const fetchProfile = async () => {
    try {
      const response = await api.get('/api/v1/client-portal/profile')
      setProfile(response.data)
    } catch {
      toast.error('Failed to load profile')
    }
  }

  const fetchLetters = async () => {
    setLoading(true)
    try {
      const response = await api.get(`/api/v1/client-portal/letters?week_offset=${weekOffset}`)
      setLetters(response.data.letters)
      setWeekStart(response.data.weekStart)
      setWeekEnd(response.data.weekEnd)
    } catch {
      toast.error('Failed to load letters')
    } finally {
      setLoading(false)
    }
  }

  const viewLetter = async (letterId: number) => {
    setLetterLoading(true)
    try {
      const response = await api.get(`/api/v1/client-portal/letters/${letterId}`)
      setSelectedLetter(response.data)
    } catch {
      toast.error('Failed to load letter')
    } finally {
      setLetterLoading(false)
    }
  }

  const downloadPdf = async (letterId: number) => {
    setDownloadingId(letterId)
    try {
      const response = await api.get(`/api/v1/client-portal/letters/${letterId}/pdf`, {
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const filename =
        response.headers['content-disposition']?.split('filename="')?.[1]?.replace('"', '') ||
        `letter-${letterId}.pdf`
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      toast.error('Failed to download PDF')
    } finally {
      setDownloadingId(null)
    }
  }

  const fetchAdditionalLetters = async () => {
    setAdditionalLoading(true)
    try {
      const response = await api.get('/api/v1/client-portal/additional-letters')
      setAdditionalLetters(response.data.letters)
    } catch {
      // Silent fail — section just won't show
    } finally {
      setAdditionalLoading(false)
    }
  }

  const requestAdditionalLetter = async (letterId: number) => {
    setRequestingId(letterId)
    try {
      await api.post(`/api/v1/client-portal/additional-letters/${letterId}/request`, {})
      toast.success('Letter requested successfully')
      fetchAdditionalLetters()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to request letter')
    } finally {
      setRequestingId(null)
    }
  }

  const goToPreviousWeek = () => {
    if (weekOffset > -52) setWeekOffset(weekOffset - 1)
  }

  const goToNextWeek = () => {
    if (weekOffset < 0) setWeekOffset(weekOffset + 1)
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-100">
          {profile ? profile.companyName : 'My Letters'}
        </h1>
        {profile && (
          <div className="flex items-center gap-4 mt-2">
            <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30 capitalize">
              {profile.tradeType.replace(/_/g, ' ')}
            </span>
            {profile.territories.length > 0 && (
              <div className="flex items-center gap-1 text-sm text-slate-400">
                <MapPin className="w-3.5 h-3.5" />
                {profile.territories.join(', ')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Business Cards */}
      {profile?.businessCardsUrl && (
        <div className="card p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2.5 rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20">
                <CreditCard className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-100">Professional Business Cards</h3>
                <p className="text-xs text-slate-400 mt-0.5">Order branded business cards to complement your prospecting letters</p>
              </div>
            </div>
            <a
              href={profile.businessCardsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary flex items-center gap-2 text-sm"
            >
              Order Now
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      )}

      {/* Week Navigator */}
      <div className="card p-4 mb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={goToPreviousWeek}
            disabled={weekOffset <= -52}
            className="p-2 rounded-lg hover:bg-slate-700/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-slate-300" />
          </button>
          <div className="text-center">
            <p className="text-lg font-semibold text-slate-100">
              {weekStart && weekEnd ? formatWeekRange(weekStart, weekEnd) : 'This Week'}
            </p>
            <p className="text-xs text-slate-500">
              {weekOffset === 0
                ? 'Current week'
                : `${Math.abs(weekOffset)} week${Math.abs(weekOffset) > 1 ? 's' : ''} ago`}
            </p>
          </div>
          <button
            onClick={goToNextWeek}
            disabled={weekOffset >= 0}
            className="p-2 rounded-lg hover:bg-slate-700/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-slate-300" />
          </button>
        </div>
      </div>

      {/* Letters */}
      {loading ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#f39c13]"></div>
        </div>
      ) : letters.length === 0 ? (
        <div className="card text-center py-12">
          <Mail className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-200 mb-2">No letters this week</h3>
          <p className="text-slate-400">
            {weekOffset === 0
              ? 'New letters will appear here once they are approved.'
              : 'No letters were approved during this week.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-400">
            {letters.length} letter{letters.length !== 1 ? 's' : ''} this week
          </p>
          {letters.map((letter) => {
            const config = STATUS_CONFIG[letter.status] || STATUS_CONFIG.approved
            const StatusIcon = config.icon
            return (
              <div
                key={letter.id}
                className="card hover:border-slate-600/50 transition-all duration-200 cursor-pointer"
                onClick={() => viewLetter(letter.id)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      {profile?.logoPath ? (
                        <img
                          src={profile.logoPath.startsWith('/') ? profile.logoPath : `/${profile.logoPath}`}
                          alt=""
                          className="w-8 h-8 object-contain rounded flex-shrink-0"
                          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden') }}
                        />
                      ) : null}
                      {!profile?.logoPath && <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />}
                      <p className="text-slate-100 font-medium truncate">
                        {letter.propertyAddress || 'Unknown address'}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      {letter.territoryName && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {letter.territoryName}
                        </span>
                      )}
                      <span>Approved {formatDate(letter.approvedAt)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${config.bg} ${config.text}`}
                    >
                      <StatusIcon className="w-3 h-3" />
                      {config.label}
                    </span>
                    {letter.applicationPdfPath && letter.applicationId && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation()
                          try {
                            // SEC-001: fetch via authed portal endpoint
                            const resp = await api.get(
                              `/api/v1/client-portal/applications/${letter.applicationId}/source-pdf`,
                              { responseType: 'blob' },
                            )
                            const url = URL.createObjectURL(resp.data)
                            window.open(url, '_blank')
                          } catch (err: any) {
                            toast.error(err.response?.data?.detail || 'Failed to load planning PDF')
                          }
                        }}
                        className="p-2 rounded-lg text-slate-400 hover:text-[#f39c13] hover:bg-slate-800/50 transition-colors"
                        title="View planning application"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); viewLetter(letter.id) }}
                      className="p-2 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-slate-800/50 transition-colors"
                      title="View letter"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {letter.hasPdf && (
                      <button
                        onClick={(e) => { e.stopPropagation(); downloadPdf(letter.id) }}
                        disabled={downloadingId === letter.id}
                        className="p-2 rounded-lg text-slate-400 hover:text-emerald-400 hover:bg-slate-800/50 transition-colors disabled:opacity-50"
                        title="Download PDF"
                      >
                        {downloadingId === letter.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Additional Letters Available */}
      {additionalLoading && (
        <div className="flex justify-center p-8 mt-8">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-orange-400"></div>
        </div>
      )}
      {!additionalLoading && additionalLetters.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-orange-500/20">
              <Gift className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-100">Additional Letters Available</h2>
              <p className="text-xs text-slate-400">Extra prospecting letters available for your territories</p>
            </div>
          </div>
          <div className="space-y-3">
            {additionalLetters.map((letter) => {
              const config = STATUS_CONFIG[letter.status] || STATUS_CONFIG.additional
              const StatusIcon = config.icon
              const isRequested = letter.status === 'additional_requested'
              return (
                <div
                  key={letter.id}
                  className="card hover:border-slate-600/50 transition-all duration-200 cursor-pointer"
                  onClick={() => viewLetter(letter.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        {profile?.logoPath ? (
                          <img
                            src={profile.logoPath.startsWith('/') ? profile.logoPath : `/${profile.logoPath}`}
                            alt=""
                            className="w-8 h-8 object-contain rounded flex-shrink-0"
                            onError={(e) => { e.currentTarget.style.display = 'none' }}
                          />
                        ) : (
                          <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                        <p className="text-slate-100 font-medium truncate">
                          {letter.propertyAddress || 'Unknown address'}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        {letter.territoryName && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {letter.territoryName}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${config.bg} ${config.text}`}
                      >
                        <StatusIcon className="w-3 h-3" />
                        {config.label}
                      </span>
                      {!isRequested && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            requestAdditionalLetter(letter.id)
                          }}
                          disabled={requestingId === letter.id}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition-colors disabled:opacity-50"
                        >
                          {requestingId === letter.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            'Request'
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Letter Content Modal — A4-style preview matching internal editor */}
      {(selectedLetter || letterLoading) && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden border border-slate-700">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between bg-slate-800/50 flex-shrink-0">
              {letterLoading ? (
                <div className="text-slate-400 text-sm">Loading letter...</div>
              ) : selectedLetter ? (
                <div>
                  <h2 className="text-lg font-semibold text-slate-100">
                    {selectedLetter.propertyAddress || 'Letter'}
                  </h2>
                  <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
                    {selectedLetter.territoryName && <span>{selectedLetter.territoryName}</span>}
                    <span>Approved: {formatDate(selectedLetter.approvedAt)}</span>
                    {selectedLetter.printedAt && <span>Printed: {formatDate(selectedLetter.printedAt)}</span>}
                    {selectedLetter.mailedAt && <span>Mailed: {formatDate(selectedLetter.mailedAt)}</span>}
                  </div>
                </div>
              ) : null}
              <button
                onClick={() => setSelectedLetter(null)}
                className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* A4 Letter Preview */}
            <div className="flex-1 overflow-auto p-6 bg-slate-700/30">
              {letterLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#f39c13]"></div>
                </div>
              ) : selectedLetter ? (
                <div className="bg-white rounded-lg shadow-lg mx-auto" style={{ width: '794px', minHeight: '1123px' }}>
                  {/* Letter Header with Logo */}
                  <div className="pt-10 px-16 pb-4">
                    <div className="flex justify-end">
                      {profile?.logoPath ? (
                        <img
                          src={profile.logoPath.startsWith('/') ? profile.logoPath : `/${profile.logoPath}`}
                          alt={profile.companyName}
                          className="max-h-24 max-w-[280px] object-contain"
                          onError={(e) => { e.currentTarget.style.display = 'none' }}
                        />
                      ) : null}
                    </div>
                  </div>

                  {/* Letter Content */}
                  <div className="px-16 pb-8">
                    <div
                      className="letter-content min-h-[700px]"
                      dangerouslySetInnerHTML={{
                        __html: selectedLetter.content.includes('<p>')
                          ? selectedLetter.content
                          : selectedLetter.content.replace(/\n/g, '<br>')
                      }}
                    />
                  </div>

                  {/* Portfolio Images & Website Footer */}
                  {(portfolioImages.some(Boolean) || profile?.websiteUrl) && (
                    <div className="px-16 pb-10">
                      <div className="border-t border-gray-300 mb-4"></div>

                      {portfolioImages.some(Boolean) && (
                        <div className="mb-4">
                          <div className="flex gap-4 justify-center">
                            {portfolioImages.map((img, idx) =>
                              img ? (
                                <div key={idx} className="w-1/3 max-w-[180px]">
                                  <img
                                    src={img.startsWith('/') ? img : `/${img}`}
                                    alt={`Portfolio ${idx + 1}`}
                                    className="w-full h-28 object-cover rounded shadow-sm"
                                    onError={(e) => { e.currentTarget.style.display = 'none' }}
                                  />
                                </div>
                              ) : null
                            )}
                          </div>
                        </div>
                      )}

                      {profile?.websiteUrl && (
                        <>
                          <div className="border-t border-gray-300 mb-3"></div>
                          <div className="text-center text-gray-500 text-sm flex items-center justify-center gap-2">
                            <Globe className="w-4 h-4" />
                            {profile.websiteUrl}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between bg-slate-800/50 flex-shrink-0">
              <div>
                {selectedLetter?.applicationPdfPath && selectedLetter?.applicationId && (
                  <button
                    onClick={async () => {
                      try {
                        // SEC-001: fetch via authed portal endpoint
                        const resp = await api.get(
                          `/api/v1/client-portal/applications/${selectedLetter.applicationId}/source-pdf`,
                          { responseType: 'blob' },
                        )
                        const url = URL.createObjectURL(resp.data)
                        window.open(url, '_blank')
                      } catch (err: any) {
                        toast.error(err.response?.data?.detail || 'Failed to load planning PDF')
                      }
                    }}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Planning Application
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => selectedLetter && downloadPdf(selectedLetter.id)}
                  disabled={!selectedLetter || downloadingId === selectedLetter?.id}
                  className="btn-primary flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </button>
                <button
                  onClick={() => setSelectedLetter(null)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
